import { AlertTriangle, ChevronLeft, ExternalLink, ImageOff, Layers3, Pencil, UtensilsCrossed, Utensils } from "lucide-react";
import { Link } from "react-router-dom";
import { getMenuThemeMeta } from "../../config/menuThemes";
import SectionIcon from "./SectionIcon";

import { formatPersianNumber as numberFormatter } from "../../utils/persianNumber";
const dateFormatter = new Intl.DateTimeFormat("fa-IR", {
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const statusClasses = {
  success: "bg-secondary_1 text-secondary",
  warning: "bg-amber-50 text-amber-800",
  neutral: "bg-gray-100 text-gray-700",
};

const formatUpdatedAt = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : dateFormatter.format(date);
};

const buildIssueMessage = ({ missingImages, incompleteItems }) => {
  const parts = [];
  if (incompleteItems > 0) parts.push(`${numberFormatter(incompleteItems)} آیتم اطلاعات ناقص دارد`);
  if (missingImages > 0) parts.push(`${numberFormatter(missingImages)} آیتم تصویر ندارد`);
  return parts.join(" و ");
};

export default function MenuOverviewSummary({ overview, statusTone, publicMenuPath, actions }) {
  const updatedAt = formatUpdatedAt(overview.updatedAt);
  const themeMeta = getMenuThemeMeta(overview.menu?.theme);
  const editPath = `/dashboard/edit-menu/${overview.menuId}`;
  const { counts } = overview;
  const stats = [
    { id: "categories", label: "دسته‌بندی", value: counts.categories, icon: Layers3, tone: "green" },
    { id: "items", label: "آیتم", value: counts.items, icon: Utensils, tone: "green" },
    { id: "images", label: "بدون تصویر", value: counts.missingImages, icon: ImageOff, tone: counts.missingImages > 0 ? "amber" : "gray" },
    { id: "incomplete", label: "ناقص", value: counts.incompleteItems, icon: AlertTriangle, tone: counts.incompleteItems > 0 ? "amber" : "gray" },
  ];
  const issueMessage = buildIssueMessage(counts);

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5" aria-labelledby="menu-overview-title">
      <div className="flex items-start gap-3">
        <SectionIcon icon={UtensilsCrossed} tone="orange" />
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 id="menu-overview-title" className="truncate text-lg font-extrabold text-accent">{overview.title}</h2>
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${statusClasses[statusTone] || statusClasses.neutral}`}>
              {overview.statusLabel}
            </span>
          </div>
          <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="flex gap-0.5" aria-hidden="true">
                {themeMeta.colors.map((color) => (
                  <span key={color} className="h-3 w-3 rounded-full border border-black/10" style={{ backgroundColor: color }} />
                ))}
              </span>
              {themeMeta.label}
            </span>
            {updatedAt && <span>بروزرسانی: {updatedAt}</span>}
          </p>
        </div>
      </div>

      <dl className="mt-5 grid grid-cols-2 gap-y-3 border-y border-gray-100 py-3 sm:grid-cols-4 sm:divide-x sm:divide-x-reverse sm:divide-gray-100">
        {stats.map((stat) => (
          <div key={stat.id} className="flex items-center justify-center gap-2.5">
            <SectionIcon icon={stat.icon} tone={stat.tone} size="sm" />
            <div className="flex flex-col">
              <dd className={`text-lg font-black leading-6 ${stat.tone === "amber" ? "text-amber-600" : "text-accent"}`}>{numberFormatter(stat.value)}</dd>
              <dt className="text-[11px] text-gray-500">{stat.label}</dt>
            </div>
          </div>
        ))}
      </dl>

      {issueMessage && (
        <Link
          to={editPath}
          state={overview.menu}
          className="mt-3 flex items-center gap-2 rounded-lg px-1 py-1.5 text-xs font-medium text-amber-800 hover:text-amber-900 focus:outline-none focus:ring-2 focus:ring-amber-100"
        >
          <AlertTriangle size={14} className="shrink-0" aria-hidden="true" />
          <span className="flex-1">{issueMessage}</span>
          <span className="inline-flex shrink-0 items-center gap-0.5 font-bold">
            رفع در ویرایشگر
            <ChevronLeft size={14} aria-hidden="true" />
          </span>
        </Link>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Link to={editPath} state={overview.menu} className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg bg-primary px-4 text-sm font-bold text-white transition-colors hover:bg-[#e8731f] focus:outline-none focus:ring-4 focus:ring-primary/20">
          <Pencil size={14} aria-hidden="true" />
          ویرایش منو
        </Link>
        {publicMenuPath && (
          <Link to={publicMenuPath} className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border border-gray-200 px-3.5 text-sm font-bold text-accent hover:bg-gray-50 focus:outline-none focus:ring-4 focus:ring-gray-100">
            <ExternalLink size={14} aria-hidden="true" />
            مشاهده
          </Link>
        )}
        {actions}
      </div>
    </section>
  );
}
